/**
 * Ein TO-DO auf einen Vorgang ziehen (PO 22.09.2026: „Drag and drop
 * Funktionalität bitte bei To-dos, wenn ausgeklappt, auch enablen").
 *
 * Schwester von `vorgangZug.ts`, und aus demselben Grund mit eigenem MIME-Typ:
 * Beim ÜBERFAHREN gibt der Browser die Nutzlast nicht heraus (`getData` ist
 * während `dragover` leer), die Typen aber schon. Nur so lässt sich vorher
 * entscheiden, ob eine Zeile den Zug überhaupt annimmt.
 *
 * NICHT zu verwechseln mit `detail/todoDrop.ts` — das ist die EMPFANGS-Seite
 * einer To-do-Karte (eine Mail oder ein Dokument auf ein To-do ziehen). Hier
 * geht es um das To-do selbst als Fracht.
 *
 * ══ DIE ZIELMATRIX ═══════════════════════════════════════════════════════
 * | Fracht  | Ziel                        | Was passiert                    |
 * |---------|-----------------------------|---------------------------------|
 * | To-do   | fremder Vorgang             | wechselt die Heimat, Strang mit |
 * | To-do   | sein eigener Eltern-Vorgang | nichts — es liegt schon dort    |
 * | To-do   | To-do DESSELBEN Vorgangs    | Zusammenführen-Dialog, Ziel =   |
 * |         |                             | das getroffene To-do, Betreff   |
 * |         |                             | vom gezogenen (s. u.)           |
 * | To-do   | To-do eines ANDEREN Vorgangs| gesperrt (Backend: 403)         |
 * | Vorgang | ein To-do                   | gesperrt (kein Weg im Backend)  |
 *
 * ══ TO-DO AUF TO-DO (PO 28.09.2026: „zusammenführen der To-dos über Drag
 * and Drop") ═══════════════════════════════════════════════════════════════
 * Bis dahin gesperrt, mit zwei Gründen — beide gelten weiter, beide sind
 * jetzt anders beantwortet:
 *
 *  · Das Backend führt zwei To-dos nur INNERHALB desselben Vorgangs zusammen
 *    (sonst 403). Deshalb nimmt ein To-do nur Geschwister an
 *    (`darfAufTodo`); die Kinder fremder Vorgänge leuchten gar nicht erst auf.
 *  · Beim Zusammenführen entscheidet man Feld für Feld (Betreff, Frist,
 *    Beteiligte). Der Zug überspringt das deshalb NICHT: Er öffnet denselben
 *    Dialog wie das ⋯-Menü, nur mit dem getroffenen To-do als Ziel
 *    vorausgewählt. Entschieden wird weiterhin dort.
 *  · Betreff (T-15, 28.09.2026): Anders als beim ⋯-Weg schlägt der Dialog
 *    beim Ziehen den Betreff des GEZOGENEN To-dos vor
 *    (`betreffVon="quelle"`). Das getroffene bleibt technisch bestehen
 *    (Id, Verlauf) und bekommt nur diesen Betreff.
 */

export const ZIEHTYP_TODO = "application/x-kmvs-todo";

/** Was mitreist: die Id für die Handlung, der Betreff für die Rückfrage. */
export interface TodoZug {
    id: number;
    titel: string;
    /** Der Vorgang, in dem das To-do heute zu Hause ist — für „liegt schon dort". */
    vorgangId: number | null;
}

/** Trägt dieser Zug ein To-do? Beantwortbar schon beim `dragover`. */
export function istTodoZug(typen: readonly string[] | DOMStringList | undefined): boolean {
    if (!typen) return false;
    for (let i = 0; i < typen.length; i++) {
        if (typen[i] === ZIEHTYP_TODO) return true;
    }
    return false;
}

/**
 * Darf dieses To-do auf jenen Vorgang?
 *
 * Auf seinen eigenen Eltern-Vorgang nicht — es liegt ja schon dort, und der
 * Server würde nur dieselbe Zeile neu schreiben. Wichtiger ist, dass die Zeile
 * beim Ziehen gar nicht erst aufleuchtet: Sonst sieht es aus, als geschähe
 * etwas, und nach dem Loslassen passiert nichts.
 */
export function darfTodoHierher(
    gezogenVorgangId: number | null | undefined,
    zielVorgangId: number,
): boolean {
    return gezogenVorgangId !== zielVorgangId;
}

/**
 * Die Rückfrage. Sie nennt beide Seiten und sagt, was das To-do VERLÄSST —
 * „nach B verschieben" allein verschweigt, dass es in A danach fehlt.
 */
export function verschiebeFrage(todoTitel: string, zielTitel: string): string {
    const t = todoTitel.trim() || "(ohne Betreff)";
    const z = zielTitel.trim() || "(ohne Betreff)";
    return `„${t}“ nach „${z}“ verschieben?`;
}

/**
 * Was dabei passiert, in der Sprache des Anwenders. Der zweite Absatz ist der
 * wichtige: Ein To-do nimmt seinen ganzen Schriftwechsel mit — wer das nicht
 * weiß, glaubt, er verschiebe nur eine Zeile.
 */
export function verschiebeErklaerung(
    todoTitel: string,
    quelleTitel: string,
    zielTitel: string,
): string {
    const t = todoTitel.trim() || "(ohne Betreff)";
    const q = quelleTitel.trim() || "(ohne Betreff)";
    const z = zielTitel.trim() || "(ohne Betreff)";
    return (
        `„${t}“ steht danach in „${z}“ — mit allem, was daran hängt: Notizen, Mails, ` +
        `Anrufe, Termine und Dokumente.\n\n` +
        `In „${q}“ ist es dann nicht mehr zu finden.`
    );
}

/**
 * Darf das gezogene To-do auf jenes To-do (PO 28.09.2026)?
 *
 * Nur ein GESCHWISTER — ein anderes To-do desselben Vorgangs. Auf sich selbst
 * nicht (nichts zu tun), auf ein fremdes Kind nicht (das Backend führt nur
 * innerhalb eines Vorgangs zusammen). Ohne bekannten Heimat-Vorgang auch
 * nicht: Dann lässt sich die Frage nicht beantworten, und ein Aufleuchten,
 * auf das ein 403 folgt, ist schlimmer als keines.
 */
export function darfAufTodo(
    gezogen: { id: number; vorgangId: number | null | undefined } | null | undefined,
    ziel: { id: number; vorgangId: number | null | undefined },
): boolean {
    if (!gezogen || gezogen.vorgangId == null || ziel.vorgangId == null) return false;
    return gezogen.id !== ziel.id && gezogen.vorgangId === ziel.vorgangId;
}
